import { usePatternData } from '../../hooks/usePatternData';
import { computeInsights } from '../../utils/insightEngine';

// ── Category metadata ──────────────────────────────────────────────────────

const CATEGORIES = [
  {
    key: 'feeding',
    label: '🍼 Feeding',
    titles: ['Feeding interval', 'Daily feed count', 'Night feeds', 'Daily milk intake'],
  },
  {
    key: 'sleep',
    label: '😴 Sleep',
    titles: ['Nap duration', 'Naps per day', 'Night sleep', 'Longest sleep stretch'],
  },
  {
    key: 'nappies',
    label: '🧷 Nappies',
    titles: ['Nappy frequency', 'Dirty nappies'],
  },
];

// ── Trend badge ────────────────────────────────────────────────────────────

const TREND_CONFIG = {
  up:      { arrow: '↑', color: 'var(--chip-green-text)',  bg: 'var(--chip-green-bg)'  },
  down:    { arrow: '↓', color: 'var(--chip-red-text)',    bg: 'var(--chip-red-bg)'    },
  neutral: { arrow: '→', color: 'var(--text-light)',       bg: 'var(--bg-light)'       },
};

function TrendBadge({ trend, sentiment }) {
  const key = sentiment === 'neutral' ? 'neutral' : trend;
  const { arrow, color, bg } = TREND_CONFIG[key] ?? TREND_CONFIG.neutral;
  return (
    <span className="insight-trend-badge" style={{ background: bg, color }}>
      {arrow}
    </span>
  );
}

// ── Individual insight card ────────────────────────────────────────────────

function InsightCard({ icon, title, body, trend, sentiment }) {
  const showBadge = trend !== 'neutral' || sentiment !== 'neutral';
  return (
    <div className="insight-card">
      <div className="insight-card-header">
        <div className="insight-card-heading">
          <span className="insight-card-icon">{icon}</span>
          <span className="insight-card-title">{title}</span>
        </div>
        {showBadge && <TrendBadge trend={trend} sentiment={sentiment} />}
      </div>
      <p className="insight-card-body">{body}</p>
    </div>
  );
}

// ── Skeleton loader ────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="insight-card-skeleton" aria-hidden="true">
      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
        <div className="skeleton-line" style={{ width: 20, height: 20, borderRadius: 6 }} />
        <div className="skeleton-line" style={{ width: '55%', height: 12 }} />
      </div>
      <div className="skeleton-line" style={{ width: '90%', height: 11 }} />
      <div className="skeleton-line" style={{ width: '70%', height: 11 }} />
    </div>
  );
}

// ── Category group ─────────────────────────────────────────────────────────

function InsightGroup({ label, insights }) {
  if (insights.length === 0) return null;
  return (
    <div className="insight-group">
      <div className="insight-group-label">{label}</div>
      <div className="insight-cards-grid">
        {insights.map((insight) => (
          <InsightCard key={insight.title} {...insight} />
        ))}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

const InsightCards = ({ childId }) => {
  const data = usePatternData(childId);

  if (data.loading) {
    return (
      <>
        {CATEGORIES.map((cat) => (
          <div key={cat.key} className="insight-group">
            <div className="insight-group-label">{cat.label}</div>
            <div className="insight-cards-grid">
              <SkeletonCard />
              <SkeletonCard />
            </div>
          </div>
        ))}
      </>
    );
  }

  const insights = computeInsights(data);
  if (insights.length === 0) return null;

  // Bucket insights into categories; anything unmatched goes into the last category
  const buckets = CATEGORIES.map((cat) => ({
    ...cat,
    insights: insights.filter((i) => cat.titles.includes(i.title)),
  }));

  return (
    <>
      {buckets.map((cat) => (
        <InsightGroup key={cat.key} label={cat.label} insights={cat.insights} />
      ))}
    </>
  );
};

export default InsightCards;
